import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, CURRENCIES } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sparkles, CheckSquare, ArrowRight, FileCheck, RefreshCw, Shield } from "lucide-react";
import { toast } from "sonner";
import MarkdownOutput from "../MarkdownOutput";
import EngagementLetterPanel from "./EngagementLetterPanel";
import ConflictCheckPanel from "./ConflictCheckPanel";

export function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
            {children}
        </div>
    );
}


function OriginBanner({ deal }) {
    const src = deal.source;
    if (!src) return null;
    const ctx = src.context || {};
    const label = src.type === "distressed" ? "Closvio Distressed" : "Closvio Sourcing";
    const d = ctx.diagnosis;
    const cf = ctx.cash_flow;
    return (
        <div className="border border-[#E2E8F0] bg-[#F8FAFC] rounded-md px-4 py-3 text-[13px] text-[#475569] mb-4" data-testid="deal-origin">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span><b className="text-[#0B1F3A]">Originated from {label}</b>{src.company_number ? ` · ${src.company_number}` : ""}</span>
                {src.path && <a className="underline text-[#0B1F3A]" href={src.path}>Open source record</a>}
                {src.figures_estimated && <span className="text-amber-700">Revenue and EBITDA are estimates, not filed figures</span>}
            </div>
            {(d || cf || ctx.distress_score != null) && (
                <div className="mt-1 flex flex-wrap gap-x-4 text-[12px]">
                    {ctx.distress_score != null && <span>Distress score {ctx.distress_score}{ctx.trajectory ? ` (${ctx.trajectory})` : ""}</span>}
                    {d && <span>Liquidity: {d.liquidity || "n/a"} · Structure: {d.structural || "n/a"} · {d.red_flag_count} red flag{d.red_flag_count === 1 ? "" : "s"}</span>}
                    {cf && <span>Cash flow “{cf.name}”: {cf.first_negative_week ? `cash negative in week ${cf.first_negative_week}` : "no cash shortfall in 13 weeks"}</span>}
                </div>
            )}
        </div>
    );
}

export default function OverviewTab({ deal, onSave }) {
    const { user } = useAuth();
    const [form, setForm] = useState({ ...deal });
    const [pmLoading, setPmLoading] = useState(false);
    const [postMortem, setPostMortem] = useState("");
    const [engagementLoading, setEngagementLoading] = useState(false);
    const [healthData, setHealthData] = useState(null);
    const [healthLoading, setHealthLoading] = useState(false);
    const [readinessBadge, setReadinessBadge] = useState(null);
    const [sellerContacts, setSellerContacts] = useState([]);
    const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

    const isClosed = form.stage === "Completed" || form.stage === "Lost";

    const fetchHealth = async () => {
        if (isClosed) return;
        setHealthLoading(true);
        try {
            const { data } = await api.get(`/deals/${deal.id}/health`);
            setHealthData(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load health score");
        } finally {
            setHealthLoading(false);
        }
    };

    // Auto-fetch on mount for active deals + seller contacts
    useEffect(() => {
        if (!isClosed) {
            fetchHealth();
            // Load cached readiness badge (no-op if not yet assessed)
            api.get(`/deals/${deal.id}/readiness`)
                .then(r => setReadinessBadge({ score: r.data.overall_score, band: r.data.band }))
                .catch(() => {});
        }
        // Fetch seller contacts for the vendor dropdown
        api.get("/contacts?type=seller")
            .then(r => {
                const all = Array.isArray(r.data) ? r.data : (r.data?.contacts || []);
                setSellerContacts(all);
            })
            .catch(() => {});
    }, [deal.id]);

    const canEditEngagement = user?.role === "admin" || user?.role === "broker";

    const toggleEngagementLetter = async () => {
        if (!canEditEngagement) return;
        setEngagementLoading(true);
        const newValue = !form.engagement_letter_signed;
        try {
            await api.patch(`/deals/${deal.id}`, { engagement_letter_signed: newValue });
            setForm((f) => ({ ...f, engagement_letter_signed: newValue }));
            toast.success("Engagement letter status updated");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to update");
        } finally {
            setEngagementLoading(false);
        }
    };

    const save = async () => {
        const payload = {
            ...form,
            deal_size: parseFloat(form.deal_size) || 0,
            probability: parseInt(form.probability) || 0,
            revenue: form.revenue ? parseFloat(form.revenue) : null,
            ebitda: form.ebitda ? parseFloat(form.ebitda) : null,
            asking_price: form.asking_price ? parseFloat(form.asking_price) : null,
            fee: form.fee ? parseFloat(form.fee) : null,
            fee_type: form.fee_type || "fixed",
            fee_percentage: form.fee_percentage ? parseFloat(form.fee_percentage) : null,
            broker_rating: form.broker_rating ? parseInt(form.broker_rating) : null,
            outcome_reason: form.outcome_reason || null,
            outcome_notes: form.outcome_notes || null,
            public_listing: !!form.public_listing,
            teaser_headline: form.teaser_headline || null,
            teaser_description: form.teaser_description || null,
            exclusivity_start: form.exclusivity_start || null,
            exclusivity_end: form.exclusivity_end || null,
            seller_contact_id: form.seller_contact_id || null,
        };
        await onSave(payload);
        toast.success("Saved");
    };
    const isLost = form.stage === "Lost";

    const runPostMortem = async (save = false) => {
        setPmLoading(true);
        try {
            const { data } = await api.post("/ai/post-mortem", { deal_id: deal.id, save_to_notes: save });
            setPostMortem(data.post_mortem);
            if (save) {
                toast.success("Saved to outcome notes");
                setForm((f) => ({ ...f, outcome_notes: (f.outcome_notes || "") + "\n\n---\nAI POST-MORTEM\n" + data.post_mortem }));
            } else {
                toast.success("Post-mortem drafted");
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to draft post-mortem");
        } finally {
            setPmLoading(false);
        }
    };
    return (
        <div className="border border-border rounded-md bg-white p-6 space-y-4 max-w-3xl">
            <OriginBanner deal={deal} />
            <div className="flex items-center justify-between -mt-1 -mb-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Deal overview</div>
                <Link
                    to={`/tasks?deal=${deal.id}`}
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-muted-foreground hover:text-gold transition-colors"
                    data-testid="view-all-tasks-link"
                >
                    <CheckSquare className="w-3 h-3" /> View all tasks <ArrowRight className="w-3 h-3" />
                </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <Field label="Company name"><Input value={form.company_name} onChange={setF("company_name")} /></Field>
                <Field label="Sector"><Input value={form.sector || ""} onChange={setF("sector")} /></Field>
                <Field label="Location"><Input value={form.location || ""} onChange={setF("location")} /></Field>
                <Field label="Lead broker"><Input value={form.lead_broker || ""} onChange={setF("lead_broker")} /></Field>
                <Field label="Deal size"><Input type="number" className="font-mono" value={form.deal_size || ""} onChange={setF("deal_size")} /></Field>
                <Field label="Currency">
                    <Select value={form.currency} onValueChange={setF("currency")}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <Field label="Revenue"><Input type="number" className="font-mono" value={form.revenue || ""} onChange={setF("revenue")} /></Field>
                <Field label={form.reported_ebitda != null ? "EBITDA (normalised)" : "EBITDA"}>
                    {form.reported_ebitda != null ? (
                        <div className="h-9 flex items-center px-3 border border-gold/40 rounded-md bg-gold/5 font-mono text-sm" data-testid="overview-ebitda-readonly">
                            <span className="text-gold font-bold">{form.normalised_ebitda != null ? fmtMoney(form.normalised_ebitda, form.currency) : "—"}</span>
                            <span className="text-muted-foreground text-xs ml-2">· Reported {fmtMoney(form.reported_ebitda, form.currency)}</span>
                        </div>
                    ) : (
                        <Input type="number" className="font-mono" value={form.ebitda || ""} onChange={setF("ebitda")} />
                    )}
                </Field>
                <Field label="Asking price"><Input type="number" className="font-mono" value={form.asking_price || ""} onChange={setF("asking_price")} /></Field>
                <Field label="Probability %"><Input type="number" min={0} max={100} className="font-mono" value={form.probability || ""} onChange={setF("probability")} /></Field>
                <Field label="Origination source">
                    <Select value={form.origination_source || "_none"} onValueChange={(v) => setForm((f) => ({ ...f, origination_source: v === "_none" ? "" : v }))}>
                        <SelectTrigger data-testid="overview-origination-source"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="_none">— Not specified</SelectItem>
                            <SelectItem value="accountant">Accountant</SelectItem>
                            <SelectItem value="solicitor">Solicitor</SelectItem>
                            <SelectItem value="wealth_manager">Wealth Manager</SelectItem>
                            <SelectItem value="broker_network">Broker Network</SelectItem>
                            <SelectItem value="direct_approach">Direct Approach</SelectItem>
                            <SelectItem value="website">Website</SelectItem>
                            <SelectItem value="linkedin">LinkedIn</SelectItem>
                            <SelectItem value="repeat_client">Repeat Client</SelectItem>
                            <SelectItem value="search_fund">Search Fund</SelectItem>
                            <SelectItem value="bought_lead">Bought Lead</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                    </Select>
                </Field>
                <Field label="Engagement letter">
                    <EngagementLetterPanel deal={deal} onDealUpdate={onSave} />
                </Field>
                <Field label="Conflict of interest check">
                    <ConflictCheckPanel deal={deal} />
                </Field>
            </div>

            {/* Exclusivity Period + Vendor Contact */}
            <div className="pt-2 border-t border-border space-y-3">
                <div className="text-xs uppercase tracking-wider text-gold font-medium">Exclusivity &amp; Vendor</div>
                <div className="grid grid-cols-3 gap-3">
                    <Field label="Exclusivity start">
                        <Input
                            type="date"
                            value={form.exclusivity_start ? form.exclusivity_start.slice(0, 10) : ""}
                            onChange={setF("exclusivity_start")}
                            data-testid="exclusivity-start-input"
                        />
                    </Field>
                    <Field label="Exclusivity end">
                        <Input
                            type="date"
                            value={form.exclusivity_end ? form.exclusivity_end.slice(0, 10) : ""}
                            onChange={setF("exclusivity_end")}
                            data-testid="exclusivity-end-input"
                        />
                    </Field>
                    <Field label="Vendor / seller contact">
                        <Select
                            value={form.seller_contact_id || "_none"}
                            onValueChange={(v) => setForm((f) => ({ ...f, seller_contact_id: v === "_none" ? null : v }))}
                        >
                            <SelectTrigger data-testid="seller-contact-select">
                                <SelectValue placeholder="— None linked" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="_none">— None linked</SelectItem>
                                {sellerContacts.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {c.name}{c.company ? ` · ${c.company}` : ""}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {sellerContacts.length === 0 && (
                            <p className="text-[10px] text-muted-foreground mt-1">
                                No seller contacts yet — add one in Contacts.
                            </p>
                        )}
                    </Field>
                </div>
            </div>

            <div className="pt-2 border-t border-border space-y-3">
                <div className="text-xs uppercase tracking-wider text-gold font-medium">Broker fee</div>
                <div className="grid grid-cols-3 gap-3">
                    <Field label="Fee type">
                        <Select value={form.fee_type || "fixed"} onValueChange={(v) => {
                            setForm((f) => ({ ...f, fee_type: v }));
                        }}>
                            <SelectTrigger data-testid="fee-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="fixed">Fixed</SelectItem>
                                <SelectItem value="percentage">% of deal size</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                    {(form.fee_type || "fixed") === "percentage" ? (
                        <>
                            <Field label="Fee %">
                                <Input
                                    type="number" step="0.1" min="0" max="100"
                                    className="font-mono"
                                    value={form.fee_percentage || ""}
                                    onChange={(e) => {
                                        const pct = parseFloat(e.target.value) || 0;
                                        const size = parseFloat(form.deal_size) || 0;
                                        setForm((f) => ({ ...f, fee_percentage: e.target.value, fee: (size * pct / 100).toFixed(2) }));
                                    }}
                                    data-testid="fee-percent-input"
                                />
                            </Field>
                            <Field label="Computed fee">
                                <Input
                                    readOnly
                                    value={form.fee ? fmtMoney(form.fee, form.currency) : "—"}
                                    className="font-mono bg-muted/40"
                                    data-testid="fee-computed-display"
                                />
                            </Field>
                        </>
                    ) : (
                        <Field label="Fee amount">
                            <Input
                                type="number" className="font-mono"
                                value={form.fee || ""}
                                onChange={setF("fee")}
                                data-testid="deal-fee-input"
                                placeholder="Commission / fee"
                            />
                        </Field>
                    )}
                </div>
            </div>
            <Field label="Business description"><Textarea rows={4} value={form.business_description || ""} onChange={setF("business_description")} /></Field>

            {!isClosed && (
                <div className="pt-4 border-t border-border space-y-3" data-testid="health-score-section">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="text-xs uppercase tracking-wider text-gold font-medium">Deal Health Score</div>
                            {readinessBadge && (
                                <div
                                    className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-medium border ${
                                        readinessBadge.band === "ready"
                                            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                            : readinessBadge.band === "developing"
                                            ? "bg-amber-50 border-amber-200 text-amber-700"
                                            : "bg-red-50 border-red-200 text-red-700"
                                    }`}
                                    data-testid="readiness-badge"
                                >
                                    <Shield className="w-3 h-3" />
                                    Readiness {readinessBadge.score}/100
                                </div>
                            )}
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={fetchHealth}
                            disabled={healthLoading}
                            className="h-7 text-xs gap-1"
                            data-testid="health-refresh-btn"
                        >
                            <RefreshCw className={`w-3 h-3 ${healthLoading ? "animate-spin" : ""}`} />
                            {healthLoading ? "Computing…" : "Refresh"}
                        </Button>
                    </div>
                    {healthData ? (
                        <>
                            <div className="flex items-center gap-4" data-testid="health-score-display">
                                <div className={`text-4xl font-bold font-mono ${
                                    healthData.health_band === "healthy" ? "text-green-600" :
                                    healthData.health_band === "at_risk" ? "text-amber-600" : "text-red-600"
                                }`}>{healthData.health_score}</div>
                                <div>
                                    <div className={`text-sm font-medium capitalize ${
                                        healthData.health_band === "healthy" ? "text-green-600" :
                                        healthData.health_band === "at_risk" ? "text-amber-600" : "text-red-600"
                                    }`} data-testid="health-band-label">
                                        {healthData.health_band?.replace("_", " ")}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground">out of 100</div>
                                </div>
                            </div>
                            <table className="w-full text-xs" data-testid="health-factors-table">
                                <thead>
                                    <tr className="text-muted-foreground uppercase tracking-wider border-b border-border/60">
                                        <th className="text-left py-1.5 font-medium">Factor</th>
                                        <th className="text-right py-1.5 font-medium w-20">Score</th>
                                        <th className="text-left py-1.5 font-medium pl-4">Detail</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {healthData.factors?.map((f) => (
                                        <tr key={f.name} className="border-b border-border/40 last:border-0">
                                            <td className="py-1.5 font-medium">{f.name}</td>
                                            <td className="py-1.5 text-right font-mono text-xs">
                                                <span className={f.score === f.max_score ? "text-green-600" : f.score === 0 ? "text-red-500" : "text-amber-600"}>
                                                    {f.score}
                                                </span>
                                                <span className="text-muted-foreground">/{f.max_score}</span>
                                            </td>
                                            <td className="py-1.5 text-muted-foreground pl-4">{f.detail}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    ) : (
                        <div className="text-sm text-muted-foreground italic" data-testid="health-no-data">
                            {healthLoading ? "Computing health score…" : "Click Refresh to compute health score"}
                        </div>
                    )}
                </div>
            )}

            {isClosed && (
                <div className="pt-4 border-t border-border space-y-3">
                    <div className="text-xs uppercase tracking-wider text-gold font-medium">
                        Outcome · {form.stage === "Completed" ? "Closing details" : "Post-mortem"}
                    </div>
                    <Field label={form.stage === "Completed" ? "Closing summary" : "Reason lost"}>
                        <Input
                            value={form.outcome_reason || ""}
                            onChange={setF("outcome_reason")}
                            placeholder={form.stage === "Completed" ? "e.g. Closed at 5.5x EBITDA to Alpha PE" : "e.g. Price mismatch, Buyer dropped, Regulatory"}
                            data-testid="outcome-reason-input"
                        />
                    </Field>
                    <Field label="Outcome notes">
                        <Textarea
                            rows={3}
                            value={form.outcome_notes || ""}
                            onChange={setF("outcome_notes")}
                            placeholder="Lessons, follow-ups, anything else for the record."
                            data-testid="outcome-notes-input"
                        />
                    </Field>
                    {form.stage === "Completed" && (
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Broker performance rating</Label>
                            <div className="flex items-center gap-1" data-testid="broker-rating-picker">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                        type="button"
                                        key={n}
                                        onClick={() => setF("broker_rating")(n)}
                                        className="p-1 hover:scale-110 transition-transform"
                                        data-testid={`rating-star-${n}`}
                                    >
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill={n <= (form.broker_rating || 0) ? "hsl(var(--gold))" : "none"} stroke={n <= (form.broker_rating || 0) ? "hsl(var(--gold))" : "hsl(var(--muted-foreground))"} strokeWidth="1.5">
                                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                        </svg>
                                    </button>
                                ))}
                                {form.broker_rating && (
                                    <button type="button" onClick={() => setF("broker_rating")(null)} className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground">clear</button>
                                )}
                            </div>
                        </div>
                    )}

                    {isLost && (
                        <div className="pt-3 border-t border-border/60 space-y-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-gold font-medium flex items-center gap-1.5">
                                        <Sparkles className="w-3 h-3" /> AI Post-mortem
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Claude Sonnet 4.5 will analyse the deal timeline, notes, and loss reason to produce a structured report.
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => runPostMortem(false)}
                                    disabled={pmLoading}
                                    data-testid="generate-postmortem-btn"
                                >
                                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                                    {pmLoading ? "Drafting…" : "Generate post-mortem"}
                                </Button>
                                {postMortem && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => runPostMortem(true)}
                                        disabled={pmLoading}
                                        data-testid="save-postmortem-btn"
                                    >
                                        Save to outcome notes
                                    </Button>
                                )}
                            </div>
                            {postMortem && (
                                <MarkdownOutput
                                    testid="postmortem-output"
                                    className="!border-gold/30 !bg-gold/5"
                                >
                                    {postMortem}
                                </MarkdownOutput>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="pt-4 border-t border-border space-y-3">
                <div className="text-xs uppercase tracking-wider text-gold font-medium">Public teaser listing</div>
                <div className="flex items-start gap-3">
                    <input
                        type="checkbox"
                        checked={!!form.public_listing}
                        onChange={(e) => setF("public_listing")(e.target.checked)}
                        className="mt-1 accent-navy"
                        data-testid="public-listing-toggle"
                    />
                    <div className="flex-1 text-sm">
                        <div className="font-medium">List on public Opportunities page</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            Anonymised teaser visible at <code className="text-gold font-mono">/opportunities</code>. Company name never shown.
                        </div>
                    </div>
                </div>
                {form.public_listing && (
                    <>
                        <Field label="Teaser headline (optional)">
                            <Input
                                value={form.teaser_headline || ""}
                                onChange={setF("teaser_headline")}
                                placeholder="e.g. Profitable UK SaaS Business · £6M Revenue"
                                data-testid="teaser-headline-input"
                            />
                        </Field>
                        <Field label="Teaser description (optional)">
                            <Textarea
                                rows={3}
                                value={form.teaser_description || ""}
                                onChange={setF("teaser_description")}
                                placeholder="Describe the opportunity for an anonymous buyer. Do not include the company name, exact location, or anything else that could identify it — the Business description field above is for internal notes only and is never shown publicly."
                                data-testid="teaser-description-input"
                            />
                            <div className="text-xs text-muted-foreground mt-1">
                                If left blank, a generic placeholder is shown instead of the internal business
                                description, so the listing stays anonymised by default.
                            </div>
                        </Field>
                    </>
                )}
            </div>
            <div className="flex justify-end">
                <Button onClick={save} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="save-deal-btn">Save changes</Button>
            </div>
        </div>
    );
}
